/**
 * User capabilities slice — server-computed permission booleans for the
 * acting user, fetched once per session from the capabilities block on
 * GET /api/odl-app/user-settings/me/.
 *
 * Replaces the dashboard display block's `permissions` section. These are
 * pure user-scoped gates (not site-scoped); the view combines them with
 * entity presence for action gating (e.g. send-command = can_send_command
 * AND unit != null). See §12 SOLIDIFIED for the full list of 13 booleans.
 *
 * Contains:
 * - capabilities slice
 * - loadCapabilities thunk
 * - selectCapabilities selector
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { extractThunkError } from '@sophocles/api/thunkUtils';
import { fetchUserSettings } from '@sophocles/api/userSettings';

/**
 * Fetch the capabilities block from the user-settings /me/ endpoint and
 * seed the slice. Skips if already loaded.
 */
export const loadCapabilities = createAsyncThunk(
  'capabilities/load',
  async (_arg, { rejectWithValue }) => {
    try {
      const data = await fetchUserSettings();
      return data.capabilities ?? {};
    } catch (e) {
      return rejectWithValue(extractThunkError(e));
    }
  },
  {
    condition: (_arg, { getState }) => {
      const { capabilities } = getState();
      return !capabilities.loaded && !capabilities.loading;
    },
  },
);

/** Default capability shape — all false until loaded. */
const DEFAULT_CAPABILITIES = {
  is_staff: false,
  can_send_command: false,
  can_clone_unit: false,
  can_manage_unit: false,
  can_clear_alarms: false,
  can_add_maintenance: false,
  can_edit_maintenance: false,
  can_edit_camera: false,
  can_delete_camera: false,
  can_capture_camera: false,
  can_add_cameraschedule: false,
  can_send_intrusion_commands: false,
};

const slice = createSlice({
  name: 'capabilities',
  initialState: {
    loaded: false,
    loading: false,
    error: null,
    /** @type {object} capability booleans keyed by capability name */
    data: { ...DEFAULT_CAPABILITIES },
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadCapabilities.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadCapabilities.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.data = { ...DEFAULT_CAPABILITIES, ...action.payload };
      })
      .addCase(loadCapabilities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to load capabilities';
      });
  },
});

// ── Selectors ────────────────────────────────────────────────────────────────

/** @returns {object} the full capabilities object */
export const selectCapabilities = state => state.capabilities.data;

const { reducer } = slice;
export default reducer;
